import { STORAGE_KEYS } from "@/constants";
import type { UserData } from "@/types";
import { logger } from "./logger";
import { setUserContext, clearUserContext } from "./monitoring";

/**
 * Secure token and user data management
 * Uses sessionStorage with encryption considerations for production
 */

class AuthStorage {
  /**
   * Store authentication token
   */
  setToken(token: string): void {
    try {
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
    } catch (error) {
      logger.error("Failed to store token", error);
    }
  }

  /**
   * Retrieve authentication token
   */
  getToken(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    } catch (error) {
      logger.error("Failed to retrieve token", error);
      return null;
    }
  }

  /**
   * Remove authentication token
   */
  removeToken(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    } catch (error) {
      logger.error("Failed to remove token", error);
    }
  }

  /**
   * Store user data
   */
  setUserData(data: UserData): void {
    try {
      sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data));
      // Set user context for error tracking
      if (data.me?.id) {
        setUserContext(data.me.id, {
          email: data.me.email,
          usertype: data.me.usertype,
        });
      }
    } catch (error) {
      logger.error("Failed to store user data", error);
    }
  }

  /**
   * Retrieve user data
   */
  getUserData(): UserData | null {
    try {
      const data = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error("Failed to retrieve user data", error);
      return null;
    }
  }

  /**
   * Remove user data
   */
  removeUserData(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
      clearUserContext();
    } catch (error) {
      logger.error("Failed to remove user data", error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!(this.getToken() && this.getUserData());
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    const userData = this.getUserData();
    return userData?.me?.usertype === "ADMIN";
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    const userData = this.getUserData();
    return userData?.me?.id || null;
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    this.removeToken();
    this.removeUserData();
  }

  /**
   * Validate token format (basic check)
   */
  isValidToken(token: string): boolean {
    // Add your token validation logic here
    // For JWT: check format, expiration, etc.
    return token.length > 0;
  }
}

export const authStorage = new AuthStorage();
